import { Inject, Injectable, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of, Subject } from 'rxjs';
import get from 'lodash.get';
import { Store } from '@ngrx/store';
import { ignoreElements, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CategorySelect, FocusOnSearch, NavigateToSearchRoute, SearchActionTypes, StartSearch } from './search.actions';
import { BuyerState } from '../state';
import { urlBuilder } from '@metromarkets/sdk';
import { Router } from '@angular/router';
import { AddSearchHistoryTerm } from '../search-suggestions/search-suggestions.actions';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '~app/modules/generic/constants';
import { APP_CATEGORY_ROUTE, APP_SEARCH_ROUTE, FeatureToggleEnum } from '~shared/constants';
import { StringUtil } from '~shared/utils/string.util';
import { isPlatformBrowser } from '@angular/common';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { FlixMediaBrandPageService } from '~shared/services/flix-media-brand-page.service';
import { DYSON_BRAND_SLUG } from '~app/modules/flixmedia-content/constants';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { environment } from '~env/environment';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

@Injectable({
  providedIn: 'root',
})
export class SearchEffects implements OnDestroy {
  destroySubject = new Subject();
  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<BuyerState>,
    @Inject(PLATFORM_ID) private platformId: Object,
    private productsListUrlService: ProductsListUrlService,
    private flixMediaBrandPageFeatureFlagService: FlixMediaBrandPageService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {}

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  @Effect()
  search$: Observable<void> = this.actions$.pipe(
    ofType(SearchActionTypes.START_SEARCH),
    map(({ payload }: StartSearch) => {
      const categoryId = get(payload, ['attrs', 'parent', 'id']);
      const nextQuery = {
        q: StringUtil.trimNotNecessarySpaces(payload.value),
        filter: {
          categories: categoryId ? { 0: payload.attrs.parent.slug } : {},
        },
      };

      return {
        nextQuery,
        payload,
      };
    }),
    tap(({ nextQuery, payload }) => {
      window.location.href = `${environment.appBaseUrl()}/${APP_SEARCH_ROUTE}?q=${encodeURIComponent(nextQuery?.q)}`;
      this.store.dispatch(new AddSearchHistoryTerm(payload));
    }),
    ignoreElements()
  );

  @Effect({ dispatch: false })
  browseToSearch$: Observable<any> = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    switchMap((action: RouterNavigatedAction) => {
      if (isPlatformBrowser(this.platformId) && this.productsListUrlService.isSearchPage()) {
        const brandSlug = get(action, 'payload.routerState.queryParams.q', '');

        this.store.dispatch(new NavigateToSearchRoute());

        if (brandSlug.toLowerCase() === DYSON_BRAND_SLUG) {
          return of(
            this.flixMediaBrandPageFeatureFlagService.getFlixMediaBrandPageForCountry(
              getCountryDomainCode(this.webAppBuyerRequest)
            )
          );
        }
        return of(false);
      }
      return of(false);
    }),
    tap((isEnabled: boolean) => {
      if (isEnabled) {
        window.location.href = `${environment.appBaseUrl()}/brand/${DYSON_BRAND_SLUG}`;
      }
    })
  );

  @Effect()
  categorySelect$: Observable<void> = this.actions$.pipe(
    ofType(SearchActionTypes.CATEGORY_SELECT),
    tap(({ payload }: CategorySelect) => {
      const queryParams = {
        offset: DEFAULT_OFFSET,
        limit: DEFAULT_LIMIT,
      };
      this.router
        .navigate([APP_CATEGORY_ROUTE, payload.slug], {
          queryParams: urlBuilder.encode(queryParams),
        })
        .then((_) => {
          this.store.dispatch(new FocusOnSearch(false));
        });
    }),
    ignoreElements()
  );

  @Effect({ dispatch: false })
  userNavigatedToSeach$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    map((action: RouterNavigatedAction) => get(action.payload, 'routerState.url')),
    tap((url: string) => {
      if (UrlUtils.isSearchPage(url)) {
        this.store.dispatch(new NavigateToSearchRoute());
      }
    })
  );
}
